import { useState, useMemo } from 'react';
import { SmartButton, DeleteButton, SaveButton } from '@/components/ui/interactive/SmartButton';
import { RealTimeUpdater } from '@/components/ui/interactive/RealTimeUpdater';
import { DynamicCard } from '@/components/ui/interactive/DynamicCard';
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { DataExport } from "@/components/ui/data-export";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Settings, RefreshCw, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { toast } from "sonner";
import { useServices, useCreateService, useUpdateService, useDeleteService } from '@/hooks/useServices';

export default function Services() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [view, setView] = useState<'table' | 'cards'>('cards');
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    price: 0,
    currency: 'SAR'
  });
  
  const { toast: legacyToast } = useToast();
  const { data: services = [], isLoading, refetch } = useServices();
  const createServiceMutation = useCreateService();
  const updateServiceMutation = useUpdateService();
  const deleteServiceMutation = useDeleteService();

  // Advanced search config
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الخدمات...",
    filters: []
  }), []);

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesSearch = service.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           service.name_en.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [services, searchTerm]);

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };

  const handleManualRefresh = async () => {
    toast.info("جاري تحديث البيانات...");
    try {
      await refetch();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  };

  const resetForm = () => {
    setFormData({
      name_ar: '',
      name_en: '',
      description_ar: '',
      description_en: '',
      price: 0,
      currency: 'SAR'
    });
    setEditingService(null);
  };

  const handleAddService = async () => {
    if (!formData.name_ar.trim() || !formData.name_en.trim()) {
      legacyToast({
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة",
        variant: "destructive"
      });
      return;
    }

    try {
      await createServiceMutation.mutateAsync({
        name_ar: formData.name_ar.trim(),
        name_en: formData.name_en.trim(),
        description_ar: formData.description_ar.trim() || null,
        description_en: formData.description_en.trim() || null,
        price: formData.price || null,
        currency: formData.currency || null
      });
      
      resetForm();
      setIsAddDialogOpen(false);
      toast.success("تم إضافة الخدمة بنجاح");
    } catch (error) {
      legacyToast({
        title: "خطأ",
        description: "حدث خطأ أثناء إضافة الخدمة",
        variant: "destructive"
      });
    }
  };

  const handleEditService = async () => {
    if (!editingService || !formData.name_ar.trim() || !formData.name_en.trim()) {
      legacyToast({
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة",
        variant: "destructive"
      });
      return;
    }

    try {
      await updateServiceMutation.mutateAsync({
        id: editingService.id,
        name_ar: formData.name_ar.trim(),
        name_en: formData.name_en.trim(),
        description_ar: formData.description_ar.trim() || null,
        description_en: formData.description_en.trim() || null,
        price: formData.price || null,
        currency: formData.currency || null
      });

      resetForm();
      setIsEditDialogOpen(false);
      toast.success("تم تحديث الخدمة بنجاح");
    } catch (error) {
      legacyToast({
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث الخدمة",
        variant: "destructive"
      });
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      await deleteServiceMutation.mutateAsync(serviceId);
      toast.success("تم حذف الخدمة بنجاح");
    } catch (error) {
      legacyToast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف الخدمة",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (service: any) => {
    setEditingService(service);
    setFormData({
      name_ar: service.name_ar,
      name_en: service.name_en,
      description_ar: service.description_ar || '',
      description_en: service.description_en || '',
      price: service.price || 0,
      currency: service.currency || 'SAR'
    });
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6" dir="rtl">
        <div className="text-center py-12">
          <div className="text-muted-foreground mb-4">
            <Settings className="w-12 h-12 mx-auto mb-4 opacity-50 animate-spin" />
            <p className="text-lg font-medium">جاري تحميل الخدمات...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <RealTimeUpdater tableName="services" onUpdate={() => handleManualRefresh()} showStatus={false}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">الخدمات</h1>
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <ViewToggle view={view} onViewChange={setView} />
            
            <DataExport 
              data={filteredServices} 
              columns={[
                { key: 'name_ar', title: 'الاسم بالعربية', dataIndex: 'name_ar' },
                { key: 'name_en', title: 'الاسم بالإنجليزية', dataIndex: 'name_en' },
                { key: 'price', title: 'السعر', dataIndex: 'price' },
                { key: 'currency', title: 'العملة', dataIndex: 'currency' }
              ]} 
              filename="services" 
              formats={['csv', 'json']} 
            />
            
            <SmartButton onClick={handleManualRefresh} variant="outline" size="sm" icon={RefreshCw}>
              تحديث
            </SmartButton>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <SmartButton 
                  icon={Plus} 
                  onClick={() => setIsAddDialogOpen(true)}
                  variant="default"
                  className="bg-primary hover:bg-primary/90"
                >
                  إضافة خدمة جديدة
                </SmartButton>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]" dir="rtl">
                <DialogHeader>
                  <DialogTitle>إضافة خدمة جديدة</DialogTitle>
                  <DialogDescription>
                    أدخل تفاصيل الخدمة الجديدة بالعربية والإنجليزية
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name_ar">الاسم بالعربية *</Label>
                    <Input 
                      id="name_ar" 
                      value={formData.name_ar} 
                      onChange={e => setFormData(prev => ({ ...prev, name_ar: e.target.value }))} 
                      placeholder="أدخل اسم الخدمة بالعربية" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name_en">الاسم بالإنجليزية *</Label>
                    <Input 
                      id="name_en" 
                      value={formData.name_en} 
                      onChange={e => setFormData(prev => ({ ...prev, name_en: e.target.value }))} 
                      placeholder="Enter service name in English" 
                      dir="ltr" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description_ar">الوصف بالعربية</Label>
                    <Input 
                      id="description_ar" 
                      value={formData.description_ar} 
                      onChange={e => setFormData(prev => ({ ...prev, description_ar: e.target.value }))} 
                      placeholder="أدخل وصف الخدمة بالعربية" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description_en">الوصف بالإنجليزية</Label>
                    <Input 
                      id="description_en" 
                      value={formData.description_en} 
                      onChange={e => setFormData(prev => ({ ...prev, description_en: e.target.value }))} 
                      placeholder="Enter service description in English" 
                      dir="ltr" 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price">السعر</Label>
                      <Input 
                        id="price" 
                        type="number" 
                        value={formData.price} 
                        onChange={e => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} 
                        placeholder="0" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currency">العملة</Label>
                      <Input 
                        id="currency" 
                        value={formData.currency} 
                        onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))} 
                        placeholder="SAR" 
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <SaveButton onClick={handleAddService}>
                    إضافة الخدمة
                  </SaveButton>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* البحث المتقدم */}
        <AdvancedSearch 
          config={searchConfig} 
          onSearch={query => handleSearch(query)} 
          initialQuery={searchTerm} 
        />

        {/* Services Grid/Cards */}
        {view === 'cards' ? (
          <div className="grid gap-4">
            {filteredServices.map(service => (
              <DynamicCard 
                key={service.id} 
                title={service.name_ar} 
                description={service.name_en} 
                hoverEffect="lift" 
                pulseOnUpdate 
                onEdit={() => openEditDialog(service)} 
                onDelete={() => handleDeleteService(service.id)}
              >
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-4">
                    <Badge variant="secondary" className="text-xs">العربية:</Badge>
                    <span className="font-medium text-card-foreground">{service.name_ar}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-xs">English:</Badge>
                    <span className="text-muted-foreground" dir="ltr">{service.name_en}</span>
                  </div>
                  {service.price && (
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className="text-xs">السعر:</Badge>
                      <span className="text-muted-foreground">{service.price} {service.currency}</span>
                    </div>
                  )}
                </div>
              </DynamicCard>
            ))}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredServices.map(service => (
              <Card key={service.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <Badge variant="secondary" className="text-xs">الاسم بالعربية:</Badge>
                        <span className="font-medium text-card-foreground">{service.name_ar}</span>
                      </div>
                      <div className="flex items-center gap-4 mb-2">
                        <Badge variant="outline" className="text-xs">الاسم بالإنجليزية:</Badge>
                        <span className="text-muted-foreground" dir="ltr">{service.name_en}</span>
                      </div>
                      {service.price && (
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className="text-xs">السعر:</Badge>
                          <span className="text-muted-foreground">{service.price} {service.currency}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <SmartButton 
                        variant="outline" 
                        size="sm" 
                        icon={Edit} 
                        onClick={() => openEditDialog(service)} 
                        className="h-8 w-8 p-0"
                      >
                        تعديل
                      </SmartButton>
                      <SmartButton 
                        variant="outline" 
                        size="sm" 
                        icon={Trash2} 
                        onClick={() => handleDeleteService(service.id)} 
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive" 
                        requireConfirmation 
                        confirmationTitle="حذف الخدمة" 
                        confirmationDescription={`هل أنت متأكد من حذف خدمة ${service.name_ar}؟`} 
                        successMessage="تم حذف الخدمة" 
                        showSuccessState
                      >
                        حذف
                      </SmartButton>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {filteredServices.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-muted-foreground mb-4">
                <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">لا توجد خدمات مضافة</p>
                <p className="text-sm">قم بإضافة الخدمات الأولى للنظام</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px]" dir="rtl">
            <DialogHeader>
              <DialogTitle>تعديل الخدمة</DialogTitle>
              <DialogDescription>قم بتعديل تفاصيل الخدمة</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name_ar">الاسم بالعربية *</Label>
                <Input 
                  id="edit-name_ar" 
                  value={formData.name_ar} 
                  onChange={e => setFormData(prev => ({ ...prev, name_ar: e.target.value }))} 
                  placeholder="أدخل اسم الخدمة بالعربية" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name_en">الاسم بالإنجليزية *</Label>
                <Input 
                  id="edit-name_en" 
                  value={formData.name_en} 
                  onChange={e => setFormData(prev => ({ ...prev, name_en: e.target.value }))} 
                  placeholder="Enter service name in English" 
                  dir="ltr" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description_ar">الوصف بالعربية</Label>
                <Input 
                  id="edit-description_ar" 
                  value={formData.description_ar} 
                  onChange={e => setFormData(prev => ({ ...prev, description_ar: e.target.value }))} 
                  placeholder="أدخل وصف الخدمة بالعربية" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description_en">الوصف بالإنجليزية</Label>
                <Input 
                  id="edit-description_en" 
                  value={formData.description_en} 
                  onChange={e => setFormData(prev => ({ ...prev, description_en: e.target.value }))} 
                  placeholder="Enter service description in English" 
                  dir="ltr" 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-price">السعر</Label>
                  <Input 
                    id="edit-price" 
                    type="number" 
                    value={formData.price} 
                    onChange={e => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} 
                    placeholder="0" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-currency">العملة</Label>
                  <Input 
                    id="edit-currency" 
                    value={formData.currency} 
                    onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))} 
                    placeholder="SAR" 
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <SaveButton onClick={handleEditService}>
                حفظ التغييرات
              </SaveButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </RealTimeUpdater>
    </div>
  );
}